import { useState, useRef, useEffect, type FC } from 'react';
import { TextInput, Select, Badge } from 'flowbite-react';
import { HiSearch, HiChevronDown } from 'react-icons/hi';
import type {
  FilterFieldDefinition,
  FilterFieldOption,
  FilterOperator,
  FilterValue,
} from '../../types/filter';
import { AgeRangeInput } from './AgeRangeInput';

interface ValueInputProps {
  field: FilterFieldDefinition;
  operator: FilterOperator;
  value: FilterValue;
  onChange: (val: FilterValue) => void;
  options?: FilterFieldOption[];
  disabled?: boolean;
}

export const ValueInput: FC<ValueInputProps> = ({
  field,
  operator,
  value,
  onChange,
  options = [],
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close multi-select dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 1. Self-contained operators require no user input
  const noInputOperators: FilterOperator[] = [
    'is_empty',
    'is_not_empty',
    'is_today',
    'is_past_7_days',
    'is_past_30_days',
  ];

  if (noInputOperators.includes(operator)) {
    return (
      <div className="flex h-9 items-center px-2">
        <Badge color="gray" size="sm" className="text-xs">
          Keine Eingabe nötig
        </Badge>
      </div>
    );
  }

  // 2. Numeric Age Range (operator 'between')
  if (field.type === 'numeric' && operator === 'between') {
    return (
      <AgeRangeInput
        value={typeof value === 'object' && value && 'min' in value ? value : null}
        onChange={(val) => onChange(val)}
        disabled={disabled}
      />
    );
  }

  // 3. Numeric single comparison (equals, >=, <=)
  if (field.type === 'numeric') {
    const numVal =
      typeof value === 'number'
        ? value
        : typeof value === 'string'
        ? value
        : '';

    return (
      <div className="w-28">
        <TextInput
          type="number"
          sizing="sm"
          placeholder="Wert"
          value={numVal}
          onChange={(e) => {
            const raw = e.target.value;
            onChange(raw === '' ? null : Number(raw));
          }}
          disabled={disabled}
          className="sentry-unmask"
        />
      </div>
    );
  }

  // 4. Date Range ('is_within')
  if (field.type === 'date' && operator === 'is_within') {
    const rangeObj =
      value && typeof value === 'object' && 'start' in value
        ? (value as { start: string; end: string })
        : { start: '', end: '' };

    return (
      <div className="flex items-center gap-1.5">
        <TextInput
          type="date"
          sizing="sm"
          value={rangeObj.start || ''}
          onChange={(e) =>
            onChange({ start: e.target.value, end: rangeObj.end || '' })
          }
          disabled={disabled}
          className="w-36 sentry-unmask"
        />
        <span className="text-xs text-gray-500 dark:text-gray-400">bis</span>
        <TextInput
          type="date"
          sizing="sm"
          value={rangeObj.end || ''}
          onChange={(e) =>
            onChange({ start: rangeObj.start || '', end: e.target.value })
          }
          disabled={disabled}
          className="w-36 sentry-unmask"
        />
      </div>
    );
  }

  // 5. Date single ('is', 'is_before', 'is_after')
  if (field.type === 'date') {
    const dateVal = typeof value === 'string' ? value : '';
    return (
      <div className="w-40">
        <TextInput
          type="date"
          sizing="sm"
          value={dateVal}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={disabled}
          className="sentry-unmask"
        />
      </div>
    );
  }

  // 6. Multi-select for Entity / Select ('is_any_of', 'is_none_of')
  if (
    (field.type === 'entity' || field.type === 'select') &&
    (operator === 'is_any_of' || operator === 'is_none_of')
  ) {
    const selectedArray: string[] = Array.isArray(value)
      ? value
      : typeof value === 'string' && value
      ? [value]
      : [];

    const filteredOptions = options.filter((opt) =>
      opt.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const toggleOption = (optVal: string) => {
      const next = selectedArray.includes(optVal)
        ? selectedArray.filter((v) => v !== optVal)
        : [...selectedArray, optVal];
      onChange(next);
    };

    const toggleAll = () => {
      if (selectedArray.length === options.length) {
        onChange([]);
      } else {
        onChange(options.map((o) => o.value));
      }
    };

    return (
      <div className="relative min-w-[200px] max-w-[320px]" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled}
          className="flex h-9 w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 text-left text-xs text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
        >
          <span className="block truncate">
            {selectedArray.length > 0
              ? `${selectedArray.length} ausgewählt`
              : 'Optionen auswählen...'}
          </span>
          <HiChevronDown
            className={`h-4 w-4 shrink-0 transition-transform ${
              isOpen ? 'rotate-180 transform' : ''
            }`}
          />
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 z-30 mt-1 max-h-60 w-64 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-700">
            {options.length > 5 && (
              <div className="border-b border-gray-200 p-2 dark:border-gray-600">
                <div className="relative">
                  <HiSearch className="absolute top-2.5 left-2.5 h-3.5 w-3.5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Suchen..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full rounded-md border border-gray-300 py-1 pr-2 pl-8 text-xs focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-600 dark:text-white"
                  />
                </div>
              </div>
            )}

            <div className="max-h-48 overflow-y-auto p-1">
              <button
                type="button"
                onClick={toggleAll}
                className="flex w-full cursor-pointer items-center rounded px-2 py-1 text-left text-xs hover:bg-gray-100 dark:hover:bg-gray-600"
              >
                <input
                  type="checkbox"
                  checked={
                    options.length > 0 && selectedArray.length === options.length
                  }
                  readOnly
                  className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-500 dark:bg-gray-600"
                />
                <span className="ml-2 font-medium text-gray-800 dark:text-gray-200">
                  Alle auswählen
                </span>
              </button>

              {filteredOptions.map((opt) => {
                const checked = selectedArray.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => toggleOption(opt.value)}
                    className="flex w-full cursor-pointer items-center rounded px-2 py-1 text-left text-xs hover:bg-gray-100 dark:hover:bg-gray-600"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      readOnly
                      className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-500 dark:bg-gray-600"
                    />
                    <span className="ml-2 truncate text-gray-700 dark:text-gray-300">
                      {opt.label}
                    </span>
                  </button>
                );
              })}

              {filteredOptions.length === 0 && (
                <div className="p-2 text-center text-xs text-gray-500">
                  Keine Optionen gefunden
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // 7. Single select for Entity / Select ('is', 'is_not')
  if (field.type === 'entity' || field.type === 'select') {
    const singleVal = typeof value === 'string' ? value : '';
    return (
      <div className="min-w-[160px] max-w-[240px]">
        <Select
          sizing="sm"
          value={singleVal}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={disabled}
        >
          <option value="">Option wählen...</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      </div>
    );
  }

  // 8. Text Input ('contains', 'starts_with', 'is', 'is_not')
  const strVal = typeof value === 'string' ? value : '';
  return (
    <div className="min-w-[160px] max-w-[260px]">
      <TextInput
        type="text"
        sizing="sm"
        placeholder="Wert eingeben..."
        value={strVal}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={disabled}
        className="sentry-unmask"
      />
    </div>
  );
};
